import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import Header from "@/components/Header";
import StickyCTA from "@/components/StickyCTA";
import GoldenParticles from "@/components/GoldenParticles";
import BirthForm from "@/components/BirthForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, track, resolveImg } from "@/lib/api";
import { Lock, Shield, Sparkles, Clock } from "lucide-react";

const DEFAULT_IMG = {
  hero: "/img/hero-lotus.png",
  diyas: "/img/temple-diyas.png",
  yantra: "/img/yantra-gold.png",
  phone: "/img/phone-wallpaper.png",
  temple: "/img/temple-marigold.png",
  meditation: "/img/meditation-diya.png",
};

export default function HomePage() {
  const { t, lang } = useLang();
  const scrollToForm = () => {
    document.getElementById("form")?.scrollIntoView({ behavior: "smooth" });
    track("hero_cta_click");
  };

  const [testimonials, setTestimonials] = React.useState([]);
  const [siteImg, setSiteImg] = React.useState({});
  const IMG = React.useMemo(() => {
    const out = { ...DEFAULT_IMG };
    Object.keys(DEFAULT_IMG).forEach((k) => { if (siteImg[k]) out[k] = resolveImg(siteImg[k]); });
    return out;
  }, [siteImg]);

  useEffect(() => {
    api.get("/settings/images").then((r) => setSiteImg(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    track("page_view", { lang });
    api.get(`/testimonials?lang=${lang}`).then((r) => setTestimonials(r.data)).catch(() => {});
  }, [lang]);

  return (
    <div className="min-h-screen bg-obsidian text-ivory relative">
      <Header onCtaClick={scrollToForm} />
      <StickyCTA onClick={scrollToForm} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16 yantra-bg">
        <img src={IMG.hero} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-50 scale-105 animate-slow-zoom" data-testid="hero-bg-image" />
        <GoldenParticles density={70} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080706]/70 via-[#080706]/35 to-[#080706]" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center animate-fade-up">
          <img src="/logo-mark.png" alt="" aria-hidden className="mx-auto w-20 sm:w-24 mb-6 drop-shadow-[0_0_30px_rgba(242,210,122,0.45)]" />
          <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-gold mb-6">{t("hero.eyebrow")}</p>
          <h1 className="font-serif-lux text-4xl sm:text-6xl lg:text-7xl font-light leading-[1.05]">
            <span className="block text-ivory">{t("hero.title.1")}</span>
            <span className="block gold-shimmer">{t("hero.title.2")}</span>
            <span className="block text-ivory">{t("hero.title.3")}</span>
          </h1>
          <p className="max-w-2xl mx-auto mt-8 text-sm sm:text-base text-muted-warm leading-relaxed">
            {t("hero.support")}
          </p>
          <div className="mt-10 mb-6">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-1">{t("hero.price.line1")}</div>
            <div className="flex items-end justify-center gap-3" data-testid="hero-price">
              <span className="font-serif-lux text-lg text-muted-warm line-through mb-1">₹199</span>
              <span className="font-serif-lux text-3xl text-gold-hi">{t("hero.price.line2")}</span>
            </div>
          </div>
          <button data-testid="hero-get-divine-image-btn" onClick={scrollToForm} className="btn-gold text-sm">
            {t("hero.cta")}
          </button>
          <p className="mt-5 text-xs text-muted-warm tracking-wider">{t("hero.trust")}</p>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-xs uppercase tracking-[0.25em] text-muted-warm">
            <FlowStep>{t("flow.details")}</FlowStep>
            <FlowArrow />
            <FlowStep>{t("flow.analysis")}</FlowStep>
            <FlowArrow />
            <FlowStep>{t("flow.image")}</FlowStep>
          </div>
        </div>
      </section>

      {/* CURIOSITY */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-gold gold-ring" data-testid="curiosity-image">
            <img src={IMG.diyas} alt="Temple diyas" loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080706]/70 to-transparent" />
          </div>
          <div className="text-left">
            <h2 className="font-serif-lux text-3xl sm:text-4xl text-ivory leading-tight">{t("curiosity.title")}</h2>
            <p className="mt-6 text-muted-warm leading-relaxed">{t("curiosity.body")}</p>
            <div className="mt-10 py-8 border-y border-gold">
              <p className="font-serif-lux text-xl sm:text-2xl text-gold-hi">{t("curiosity.statement.1")}</p>
              <p className="font-serif-lux text-xl sm:text-2xl text-ivory mt-1">{t("curiosity.statement.2")}</p>
            </div>
            <button onClick={scrollToForm} className="btn-outline-gold text-xs mt-10" data-testid="curiosity-cta-btn">{t("curiosity.cta")}</button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 px-6 max-w-6xl mx-auto">
        <h2 className="font-serif-lux text-3xl sm:text-5xl text-center gold-shimmer mb-16">{t("how.title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1,2,3,4].map((n) => (
            <div key={n} className="glass-panel rounded-2xl p-6 hover:border-gold-active transition">
              <div className="text-xs text-gold tracking-widest">0{n}</div>
              <h3 className="font-serif-lux text-lg text-ivory mt-3">{t(`how.step${n}.t`)}</h3>
              <p className="text-sm text-muted-warm mt-3 leading-relaxed">{t(`how.step${n}.d`)}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3 text-xs uppercase tracking-[0.25em] text-muted-warm">
          {[1,2,3,4,5].map((s,i,arr)=>(
            <React.Fragment key={s}>
              <span className="text-gold-hi">{t(`how.flow.${s}`)}</span>
              {i<arr.length-1 && <FlowArrow />}
            </React.Fragment>
          ))}
        </div>
        <div className="relative mt-14 rounded-3xl overflow-hidden border border-gold aspect-[21/9] sm:aspect-[3/1]" data-testid="how-banner-image">
          <img src={IMG.yantra} alt="Sacred yantra" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080706]/85 via-[#080706]/30 to-transparent" />
          <div className="absolute inset-0 flex items-center px-8 sm:px-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">{t("flow.analysis")}</p>
              <p className="font-serif-lux text-2xl sm:text-4xl text-ivory mt-2 max-w-md">{t("curiosity.statement.2")}</p>
            </div>
          </div>
        </div>
        <div className="text-center mt-12">
          <button onClick={scrollToForm} className="btn-gold text-sm">{t("how.cta")}</button>
        </div>
      </section>

      {/* FORM */}
      <section id="form" className="py-24 px-6 relative">
        <div className="max-w-4xl mx-auto">
          <BirthForm />
        </div>
      </section>

      {/* WHAT YOU GET / TRUST */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <div className="glass-panel rounded-3xl overflow-hidden grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[280px] lg:min-h-full" data-testid="purchase-image">
            <img src={IMG.phone} alt="Divine wallpaper on phone" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#0e0c09] via-transparent to-transparent" />
          </div>
          <div className="p-8 sm:p-12">
            <h3 className="font-serif-lux text-2xl sm:text-3xl text-gold-hi mb-2">{t("purchase.title")}</h3>
            <ul className="mt-6 space-y-3 text-ivory text-sm">
              {["purchase.i1","purchase.i2","purchase.i3","purchase.i4","purchase.i5"].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <span className="mt-0.5 text-gold-hi">✓</span>
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-warm">{t("purchase.onetime")}</div>
              <div className="mt-2 flex items-end gap-3" data-testid="purchase-price">
                <span className="font-serif-lux text-2xl text-muted-warm line-through mb-1.5">₹199</span>
                <span className="font-serif-lux text-5xl text-gold-hi">₹99</span>
                <span className="mb-2 text-[10px] uppercase tracking-widest bg-gold text-obsidian px-2 py-0.5 rounded-full">50% off</span>
              </div>
              <div className="text-xs uppercase tracking-[0.3em] text-muted-warm mt-2">{t("purchase.nosubs")}</div>
              <button onClick={scrollToForm} className="btn-gold text-sm mt-8">{t("locked.cta")}</button>
              <div className="mt-4 text-xs text-muted-warm tracking-wider">{t("purchase.secure")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY TRUST */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <h2 className="font-serif-lux text-3xl sm:text-4xl text-center gold-shimmer mb-14">{t("trust.title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {n:"01",k:"trust.oneprice",i:<Sparkles size={20}/>},
            {n:"02",k:"trust.personal",i:<Sparkles size={20}/>},
            {n:"03",k:"trust.secure",i:<Shield size={20}/>},
            {n:"04",k:"trust.instant",i:<Clock size={20}/>},
            {n:"05",k:"trust.access",i:<Lock size={20}/>},
            {n:"06",k:"trust.refund",i:<Shield size={20}/>},
          ].map((c) => (
            <div key={c.n} className="glass-panel rounded-2xl p-6">
              <div className="flex items-center gap-3 text-gold-hi">{c.i}<span className="text-xs tracking-widest text-gold">{c.n}</span></div>
              <h4 className="font-serif-lux text-lg text-ivory mt-3">{t(`${c.k}.t`)}</h4>
              <p className="text-sm text-muted-warm mt-2 leading-relaxed">{t(`${c.k}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="experiences" className="py-24 px-6 max-w-6xl mx-auto">
        <h2 className="font-serif-lux text-3xl sm:text-4xl text-center text-ivory">{t("testimonials.title")}</h2>
        <p className="text-center text-muted-warm text-sm mt-3 mb-10">{t("testimonials.sub")}</p>
        {testimonials.length === 0 ? (
          <div className="glass-panel rounded-3xl p-12 text-center text-muted-warm">{t("testimonials.coming")}</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
            {testimonials.map((tm) => (
              <div key={tm.id} className="flex-shrink-0 w-64 aspect-[9/16] rounded-2xl border border-gold overflow-hidden relative bg-card-warm group" data-testid={`testimonial-card-${tm.first_name.toLowerCase()}`}>
                {tm.video_url ? (
                  <video src={resolveImg(tm.video_url)} poster={tm.thumbnail_url ? resolveImg(tm.thumbnail_url) : undefined} controls playsInline preload="none" className="absolute inset-0 w-full h-full object-cover" data-testid={`testimonial-video-${tm.first_name.toLowerCase()}`} />
                ) : tm.thumbnail_url && (
                  <img src={resolveImg(tm.thumbnail_url)} alt={tm.first_name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-testid={`testimonial-photo-${tm.first_name.toLowerCase()}`} />
                )}
                <div className={`absolute inset-0 flex flex-col justify-end p-4 bg-gradient-to-t from-black/95 via-black/40 to-transparent ${tm.video_url ? "pointer-events-none" : ""}`}>
                  <div className="text-gold-hi text-xs">{tm.first_name} · {tm.city}</div>
                  <p className="text-ivory text-sm mt-1 line-clamp-3">{tm.short_quote}</p>
                  {tm.verified_purchase && <div className="mt-2 text-[10px] text-gold uppercase tracking-widest">{t("testimonials.verified")}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 7 DAYS */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <h2 className="font-serif-lux text-3xl sm:text-4xl text-center gold-shimmer mb-12">{t("seven.title")}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-2 relative rounded-3xl overflow-hidden border border-gold min-h-[320px]" data-testid="seven-image">
            <img src={IMG.meditation} alt="Quiet reflection" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080706]/90 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 font-serif-lux text-2xl text-gold-hi space-y-0.5">
              <div>{t("seven.motto.1")}</div>
              <div className="text-ivory">{t("seven.motto.2")}</div>
              <div>{t("seven.motto.3")}</div>
            </div>
          </div>
          <div className="lg:col-span-3 glass-panel rounded-3xl p-8 sm:p-12">
            <ol className="space-y-4 text-ivory">
              {["seven.i1","seven.i2","seven.i3","seven.i4","seven.i5"].map((k, i) => (
                <li key={k} className="flex gap-4">
                  <span className="w-8 h-8 flex-shrink-0 rounded-full border border-gold-active text-gold-hi flex items-center justify-center text-sm">{i+1}</span>
                  <span className="mt-1">{t(k)}</span>
                </li>
              ))}
            </ol>
            <div className="mt-10">
              <button onClick={scrollToForm} className="btn-gold text-sm">{t("seven.cta")}</button>
            </div>
          </div>
        </div>
      </section>

      {/* TRADITION */}
      <section className="relative py-32 px-6 overflow-hidden" data-testid="tradition-section">
        <img src={IMG.temple} alt="" aria-hidden loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080706] via-[#080706]/60 to-[#080706]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="font-serif-lux text-3xl sm:text-4xl gold-shimmer">{t("trad.title")}</h2>
          <p className="text-muted-warm mt-6 leading-relaxed">{t("trad.body")}</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 max-w-3xl mx-auto">
        <h2 className="font-serif-lux text-3xl sm:text-4xl text-center gold-shimmer mb-10">{t("faq.title")}</h2>
        <Accordion type="single" collapsible data-testid="faq-accordion-section">
          {[1,2,3,4,5,6,7,8,9].map((i) => (
            <AccordionItem key={i} value={`f${i}`} className="border-b border-gold">
              <AccordionTrigger className="text-ivory hover:text-gold-hi text-left text-sm sm:text-base font-serif-lux">{t(`faq.q${i}`)}</AccordionTrigger>
              <AccordionContent className="text-muted-warm text-sm leading-relaxed">{t(`faq.a${i}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL */}
      <section className="py-32 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(214,170,69,0.14)_0%,transparent_60%)]" />
        <div className="relative">
          <img src="/logo-mark.png" alt="" aria-hidden className="mx-auto w-16 mb-6 opacity-90" />
          <h2 className="font-serif-lux text-4xl sm:text-6xl text-ivory">{t("final.title")}</h2>
          <p className="mt-6 text-muted-warm max-w-xl mx-auto">{t("final.sub")}</p>
          <div className="mt-10 flex items-end justify-center gap-4" data-testid="final-price">
            <span className="font-serif-lux text-3xl text-muted-warm line-through mb-2">₹199</span>
            <span className="font-serif-lux text-6xl gold-shimmer">₹99</span>
          </div>
          <button onClick={scrollToForm} className="btn-gold text-sm mt-10" data-testid="final-cta-btn">{t("final.cta")}</button>
          <div className="mt-4 text-xs text-muted-warm tracking-wider uppercase">{t("final.trust")}</div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gold px-6 py-14 max-w-6xl mx-auto" data-testid="site-footer">
        <div className="flex flex-col sm:flex-row justify-between gap-8">
          <div>
            <img src="/logo.png" alt="SHREE RAGO" className="h-14 w-auto" />
            <div className="text-xs text-muted-warm mt-3 tracking-widest">{t("footer.tagline")}</div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-xs uppercase tracking-widest text-muted-warm sm:max-w-md sm:justify-end">
            <a href="#how" className="hover:text-gold-hi">{t("footer.how")}</a>
            <a href="#faq" className="hover:text-gold-hi">{t("footer.faq")}</a>
            <Link to={`/${lang}/access`} className="hover:text-gold-hi text-gold" data-testid="footer-access-link">{t("access.link")}</Link>
            <Link to={`/${lang}/legal/terms`} className="hover:text-gold-hi" data-testid="footer-terms-link">{t("footer.terms")}</Link>
            <Link to={`/${lang}/legal/privacy`} className="hover:text-gold-hi" data-testid="footer-privacy-link">{t("footer.privacy")}</Link>
            <Link to={`/${lang}/legal/refunds`} className="hover:text-gold-hi" data-testid="footer-refund-link">{t("footer.refund")}</Link>
            <Link to={`/${lang}/legal/shipping`} className="hover:text-gold-hi" data-testid="footer-shipping-link">{t("footer.shipping")}</Link>
          </div>
        </div>
        <p className="mt-10 text-[11px] text-muted-warm leading-relaxed max-w-3xl">
          {t("footer.disclaimer")}
        </p>
        <p className="text-[11px] text-muted-warm mt-4">© {new Date().getFullYear()} SHREE RAGO. {t("footer.rights")}</p>
      </footer>
    </div>
  );
}

function FlowStep({ children }) {
  return <span className="text-gold-hi">{children}</span>;
}
function FlowArrow() {
  return <span className="text-gold">→</span>;
}
